import { create } from "zustand";
import { getCastQueue, isNoQueue } from "../api/cast";
import type { RBMetadata } from "../hooks/useRabbitMQ";
import { parseQueue, type CastQueue, type QueueEntry } from "../lib/castQueue";
import logger from "../lib/logger";
import { slotEntries } from "../lib/streamSlot";
import { findSlot, useStreamStatusStore } from "./streamStatusStore";

/**
 * Each link slot's cast queue (spec 2026-10-16 §4), by slot id: from events (`metadata.queue`, with
 * the slot's `timelineRev`), from the caster's answers (with the answer timeline's rev), and from
 * GET queue for late joiners. Ordered by rev like the timeline (cast sync R1).
 */
export interface CastQueueState {
  bySlot: Record<number, QueueEntry>;
  /** Slots whose GET queue answered 404 (no queue, or an old room-stc), until the slot is freed. */
  missing: Record<number, true>;
  /**
   * Per slot, bumped when the cast's identity changes: the queue is dropped (STOP_STREAM, slot freed)
   * or replaced by one with another postId, count or items. `next` carries no cast identity (spec §4
   * known limit), so the advancer and ownCastStore capture this at schedule time and cancel a pending
   * error-skip timer, retry or queued automatic next when it moved. Only ever increases.
   */
  generation: Record<number, number>;
  take(slotId: number, queue: CastQueue, rev: number | null): void;
  /** 409 STALE_INDEX: the server's current queue replaces ours; the rev stays, so a later event still wins. */
  adopt(slotId: number, queue: CastQueue): void;
  drop(slotId: number): void;
  fromEvent(metadata: RBMetadata): void;
  /** A busy link slot without a stored queue asks GET queue once (late join). */
  ensure(slotId: number): void;
  reset(): void;
}

/** C53: an empty store takes anything; a rev beats none; an equal or higher rev wins (an answer and its event share one). */
export function acceptsQueue(current: QueueEntry | undefined, rev: number | null): boolean {
  if (!current) return true;
  if (rev == null) return false;
  return current.rev == null || rev >= current.rev;
}

/** The same cast: an advance changes only `index`. */
function sameCast(a: CastQueue, b: CastQueue): boolean {
  return (
    a.postId === b.postId &&
    a.count === b.count &&
    a.items.length === b.items.length &&
    a.items.every((it, i) => it.url === b.items[i].url)
  );
}

const inflight = new Set<number>();

const without = <T,>(m: Record<number, T>, key: number): Record<number, T> => {
  if (!(key in m)) return m;
  const next = { ...m };
  delete next[key];
  return next;
};

const bumped = (g: Record<number, number>, slotId: number): Record<number, number> => ({ ...g, [slotId]: (g[slotId] ?? 0) + 1 });

export const useCastQueueStore = create<CastQueueState>((set, get) => ({
  bySlot: {},
  missing: {},
  generation: {},

  take: (slotId, queue, rev) =>
    set((s) => {
      const current = s.bySlot[slotId];
      if (!acceptsQueue(current, rev)) return s;
      const changed = current != null && !sameCast(current.queue, queue);
      return { bySlot: { ...s.bySlot, [slotId]: { queue, rev } }, ...(changed ? { generation: bumped(s.generation, slotId) } : {}) };
    }),

  adopt: (slotId, queue) =>
    set((s) => {
      const current = s.bySlot[slotId];
      const changed = current != null && !sameCast(current.queue, queue);
      return { bySlot: { ...s.bySlot, [slotId]: { queue, rev: current?.rev ?? null } }, ...(changed ? { generation: bumped(s.generation, slotId) } : {}) };
    }),

  drop: (slotId) =>
    set((s) => {
      const bySlot = without(s.bySlot, slotId);
      const missing = without(s.missing, slotId);
      return bySlot === s.bySlot && missing === s.missing ? s : { bySlot, missing, generation: bumped(s.generation, slotId) };
    }),

  fromEvent: (metadata) => {
    const slot = metadata.slot;
    if (!slot || slot.id == null) return;
    if (slot.available === true) {
      get().drop(slot.id);
      return;
    }
    const queue = parseQueue(metadata.queue);
    if (queue) get().take(slot.id, queue, typeof slot.timelineRev === "number" ? slot.timelineRev : null);
  },

  ensure: (slotId) => {
    const s = get();
    if (s.bySlot[slotId] || s.missing[slotId] || inflight.has(slotId)) return;
    inflight.add(slotId);
    getCastQueue(slotId)
      .then(
        (queue) => {
          if (!queue || get().bySlot[slotId]) return; // C53c: whatever arrived meanwhile is at least as new
          const slot = findSlot(useStreamStatusStore.getState().infoByGc, slotId)?.slot;
          if (slot?.available === true) return;
          get().take(slotId, queue, typeof slot?.timelineRev === "number" ? slot.timelineRev : null);
        },
        (e: unknown) => {
          if (isNoQueue(e)) set((x) => ({ missing: { ...x.missing, [slotId]: true } }));
          else logger.warn("[CastQueue] queue fetch failed for slot", slotId, e);
        },
      )
      .finally(() => inflight.delete(slotId));
  },

  reset: () => {
    inflight.clear();
    // generation is kept, bumped: a timer scheduled before sign-out must still see a change.
    set((s) => ({ bySlot: {}, missing: {}, generation: Object.fromEntries(Object.entries(s.generation).map(([k, v]) => [k, v + 1])) }));
  },
}));

/** C54b: a slot the list shows free has no queue (STOP_STREAM carries none). */
useStreamStatusStore.subscribe((status, prev) => {
  if (status.infoByGc === prev.infoByGc) return;
  const { bySlot, missing, drop } = useCastQueueStore.getState();
  for (const info of Object.values(status.infoByGc)) {
    for (const { slot } of slotEntries(info)) {
      if (slot.available === true && (slot.id in bySlot || slot.id in missing)) drop(slot.id);
    }
  }
});

export function resetCastQueueStore(): void {
  useCastQueueStore.getState().reset();
}
